import { isApiError, type AuthSession, type User } from '@racksavant/core';
import { create } from 'zustand';
import { client, setUnauthorizedHandler, tokenStore } from '../lib/api';

/**
 * Wipes the React Query cache when the identity changes.
 *
 * Registered by `App` because the store must not import the query client.
 * Without it, signing out and back in as somebody else in the same tab renders
 * the previous account's looks, body shots and notifications straight from
 * cache — `useMe` alone has a 60s staleTime.
 */
let resetCaches: (() => void) | null = null;

export function setCacheReset(reset: (() => void) | null): void {
  resetCaches = reset;
}

export type AuthStatus = 'loading' | 'authenticated' | 'anonymous';

interface AuthState {
  status: AuthStatus;
  user: User | null;
  /** True until the very first /v1/me settles, so we never flash the sign-in page. */
  hydrated: boolean;
  setSession(session: AuthSession): void;
  setUser(user: User | null): void;
  clear(): void;
  hydrate(): Promise<void>;
  signOut(): Promise<void>;
}

export const useAuthStore = create<AuthState>((set, get) => ({
  status: 'loading',
  user: null,
  hydrated: false,

  setSession: (session) => set({ status: 'authenticated', user: session.user, hydrated: true }),

  setUser: (user) =>
    set((state) => ({
      user,
      status: user ? 'authenticated' : 'anonymous',
      hydrated: state.hydrated || user !== null,
    })),

  clear: () => {
    resetCaches?.();
    set({ status: 'anonymous', user: null, hydrated: true });
  },

  hydrate: async () => {
    if (!tokenStore.get()) {
      set({ status: 'anonymous', user: null, hydrated: true });
      return;
    }
    try {
      set({ status: 'authenticated', user: await client.me(), hydrated: true });
      return;
    } catch (error) {
      // Only a 401 means the session is actually gone. A dead network or a 5xx
      // used to sign the user out of a perfectly valid session, so give the
      // transient case exactly one more chance before giving up.
      if (isApiError(error) && error.status === 401) {
        get().clear();
        return;
      }
    }

    try {
      set({ status: 'authenticated', user: await client.me(), hydrated: true });
    } catch {
      // Tokens are deliberately left in place: a reload can still recover.
      set({ status: 'anonymous', user: null, hydrated: true });
    }
  },

  signOut: async () => {
    await client.logout();
    get().clear();
  },
}));

setUnauthorizedHandler(() => {
  resetCaches?.();
  useAuthStore.setState({ status: 'anonymous', user: null, hydrated: true });
});

export const selectUser = (state: AuthState) => state.user;
export const selectStatus = (state: AuthState) => state.status;
